import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "../Logo";
import { modules } from "@/data/modules";
import { NAV } from "@/constants/testIds";

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${isActive ? "text-[#1F1E4E]" : "text-slate-600 hover:text-[#1F1E4E]"}`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [featOpen, setFeatOpen] = useState(false);

  return (
    <header
      data-testid={NAV.container}
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            <div
              className="relative"
              onMouseEnter={() => setFeatOpen(true)}
              onMouseLeave={() => setFeatOpen(false)}
            >
              <button
                className="text-sm font-medium text-slate-600 hover:text-[#1F1E4E] flex items-center gap-1"
                data-testid="nav-features-dropdown"
              >
                Features <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {featOpen && (
                <div className="absolute top-full left-0 pt-3 w-[640px]">
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 grid grid-cols-2 gap-1">
                    {modules.map((m) => {
                      const I = m.icon;
                      return (
                        <Link
                          key={m.slug}
                          to={`/software/${m.slug}`}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                          data-testid={`nav-mega-${m.slug}`}
                        >
                          <div className="h-9 w-9 rounded-lg bg-[#E6F6EC] flex items-center justify-center shrink-0">
                            <I className="h-4.5 w-4.5 text-[#00A651]" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-[#1F1E4E]">{m.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{m.tagline}</div>
                          </div>
                        </Link>
                      );
                    })}
                    <div className="col-span-2 mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <Link to="/features" className="text-sm font-bold text-[#00A651] hover:text-[#008A43]">
                        Explore all features →
                      </Link>
                      <Link to="/custom-development" className="text-xs text-slate-500 hover:text-[#1F1E4E]">
                        Need custom features?
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/industries" className={linkClass}>Industries</NavLink>
            <NavLink to="/pricing" className={linkClass}>Pricing</NavLink>
            <NavLink to="/integrations" className={linkClass}>Integrations</NavLink>
            <NavLink to="/custom-development" className={linkClass}>Custom Dev</NavLink>
            <NavLink to="/about" className={linkClass}>About</NavLink>
          </nav>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" className="text-sm font-medium text-slate-600 hover:text-[#1F1E4E]" data-testid="nav-contact-link">
            Contact
          </Link>
          <Link
            to="/contact?intent=book_demo"
            data-testid={NAV.bookDemo}
            className="bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-5 py-2.5 text-sm transition-colors shadow-sm"
          >
            Book a Demo
          </Link>
        </div>

        <button
          className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="p-6 flex flex-col gap-4">
            {["Features", "Industries", "Pricing", "Integrations", "Custom Development", "About", "Contact"].map((l) => (
              <NavLink
                key={l}
                to={`/${l.toLowerCase().replace(/ /g, "-")}`.replace("/features", "/features")}
                className="text-sm font-medium text-slate-700"
                onClick={() => setOpen(false)}
              >
                {l}
              </NavLink>
            ))}
            <Link
              to="/contact?intent=book_demo"
              onClick={() => setOpen(false)}
              className="bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-5 py-2.5 text-sm text-center"
              data-testid="nav-mobile-book-demo"
            >
              Book a Demo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
